import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { endpoints } from "../../../api/endpoint";
import axios from "axios";
import SuccessErrorAlert from "../../../components/Alert/SuccessErrorAlert";

const OnboardingFormDialog = ({
  open,
  onClose,
  data: onboardingData,
  refetchTableData,
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const autoFilledData = useMemo(() => {
    if (!onboardingData) return null;

    const driverDetails = onboardingData.driverDetails || {};
    const loanSummary = onboardingData.loanSummary || {};
    const driverName = driverDetails.driverName || "";
    const mobileNumber = driverDetails.mobileNumber || "";

    const driverNameClean = driverName.replace(/[^a-zA-Z0-9._-]/g, "_");
    const generatedEmail = `${driverNameClean.toLowerCase()}.bp@gmail.com`;
    const phoneToUse = mobileNumber || "";

    return {
      name: driverName,
      driverId: onboardingData.driverId || "",
      city: onboardingData.city || "",
      phoneNumber: phoneToUse,
      email: generatedEmail,
      driverOrganization: loanSummary.financier || "",
      dailyEmi: loanSummary.emi.daily || "",
      inActiveDays: 0,
      vehicleCategory: "3",
      swapFeeArr: [1000, 1000, 1000, 1000],
      driverCurrentStatus: "Onboarded/Active",
      model: "RentalCharger",
    };
  }, [onboardingData]);

  useEffect(() => {
    if (open && autoFilledData) {
      setPhoneNumber(autoFilledData.phoneNumber);
      setEmail(autoFilledData.email);
    }
  }, [open, autoFilledData]);

  const handleSubmit = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      alert("Please enter a valid email address");
      return;
    }

    if (!phoneNumber || phoneNumber.length < 10) {
      alert("Please enter a valid phone number");
      return;
    }

    const submissionData = {
      name: autoFilledData.name,
      email: email,
      city: autoFilledData.city,
      driverId: autoFilledData.driverId,
      phoneNumber: phoneNumber,
      vehicleCategory: autoFilledData.vehicleCategory,
      swapFeeArr: autoFilledData.swapFeeArr,
      inActiveDays: autoFilledData.inActiveDays,
      driverCurrentStatus: autoFilledData.driverCurrentStatus,
      driverOrganization: autoFilledData.driverOrganization,
      model: autoFilledData.model,
      dailyEmi: autoFilledData.dailyEmi,
      penaltyFee: 130,
      onboardingId: onboardingData.id,
    };

    handleSubmitForm?.(submissionData);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    const result = await handleDSubmitForm(formData);

    if (!result.success) {
      setError(result.error);
    } else {
      setSuccess("Successfully created.");
    }

    setLoading(false);
  };

  const handleDSubmitForm = async (payload) => {
    try {
      const response = await axios.post(
        endpoints.createDriverOfOnboarding,
        payload
      );
      refetchTableData();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Something went wrong",
      };
    }
  };

  const handleReadError = () => {
    setError(null);
  };

  const handleReadSuccess = () => {
    setSuccess(null);
    onClose();
  };

  const DisplayField = ({ label, value }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          backgroundColor: "#f5f5f5",
          borderColor: "#e0e0e0",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value || value === 0 ? value : "--"}
        </Typography>
      </Paper>
    </Box>
  );

  if (!autoFilledData) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
        Card Addition
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <SuccessErrorAlert
          onErrorClose={handleReadError}
          onSuccessClose={handleReadSuccess}
          successMessage={success}
          errorMessage={error}
        />

        <Grid container spacing={1} mt={1}>
          <Grid item xs={12} sm={6}>
            <DisplayField label="Name" value={autoFilledData.name} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DisplayField label="Driver ID" value={autoFilledData.driverId} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DisplayField label="City" value={autoFilledData.city} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DisplayField
              label="Organization"
              value={autoFilledData.driverOrganization}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Phone Number <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter phone number"
                type="tel"
                disabled={loading}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Email <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email"
                type="email"
                disabled={loading}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DisplayField
              label="Vehicle Category"
              value={autoFilledData.vehicleCategory}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DisplayField
              label="Swap Fee Array"
              value={autoFilledData.swapFeeArr.join(", ")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DisplayField
              label="Inactive Days"
              value={autoFilledData.inActiveDays}
            />
          </Grid>

          {/* <Grid item xs={12} sm={6}>
            <DisplayField
              label="Penalty Fee"
              value={autoFilledData.penaltyFee}
            />
          </Grid> */}

          <Grid item xs={12} sm={6}>
            <DisplayField
              label="Driver Status"
              value={autoFilledData.driverCurrentStatus}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DisplayField label="Model" value={autoFilledData.model} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DisplayField label="Daily EMI" value={autoFilledData.dailyEmi} />
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Only Phone Number and Email can be edited.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          Form
          variant="contained"
          disabled={loading || success}
          endIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingFormDialog;
